import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { AclAssetPermission } from '@y42/acl/models';
import { AbstractFeatureQueryEntity, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { debounceTime, map, Observable, shareReplay } from 'rxjs';
import { DataLineageListViewData, DataLineageState, DataLineageUIModel } from './data-lineage.model';
import { DataLineageStore } from './data-lineage.store';

@Injectable({ providedIn: 'root' })
export class DataLineageQuery extends AbstractFeatureQueryEntity<DataLineageState, DataLineageUIModel> {
  constructor(protected readonly store: DataLineageStore, private readonly featureAclQuery: FeatureAclQuery) {
    super({
      store,
      featureType: Y42FeatureType.Lineage,
      toUIModel: (storeModel) => {
        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.Lineage];
        assertNonNullable(resourceType);

        return {
          ...storeModel,
          featureType: Y42FeatureType.Lineage,
          last_used: this.getValue().last_used?.[storeModel.$path] || null,
          starred: !!this.getValue().starred[storeModel.$path],
          hasReadPermission: !!this.featureAclQuery.getPermission({
            resourceType,
            assetPath: storeModel.$path,
            permission: AclAssetPermission.Read,
          })?.allowed,
        };
      },
    });
  }

  readonly active$: Observable<DataLineageUIModel | undefined> = combineQueries([
    this.selectActive(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    map(([lineage]) => (lineage ? this.toUIModel(lineage) : lineage)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly items$: Observable<DataLineageUIModel[]> = combineQueries([
    this.selectAll(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([lineages]) => lineages.map((lineage) => this.toUIModel(lineage))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly toListViewModel = (lineage: DataLineageUIModel): DataLineageListViewData[] => {
    return [{ ...lineage, id: lineage.$path }];
  };
}
